import { FC, useMemo } from 'react';

import { Formik } from 'formik';
import { type FormikHelpers } from 'formik';
import { DATA_SOURCE, VIEWDATA_TYPE } from 'utils/types';
import useModal from 'ui/partials/Modal/useModal';
import {
  useCreateViewData,
  useGetDatas,
  useGetViewDatas,
  useUpdateViewData,
} from 'modules/account/settings/hooks';

import CustomFieldsForm from '../forms/CustomFieldsForm';
import {
  CUSTOM_FIELDS_CONFIGURATOR_FORM,
  CUSTOM_FIELDS_CONFIGURATOR_SCHEMA,
} from '../forms/schema';

type PROPS = {
  source: DATA_SOURCE;
  usedOn: VIEWDATA_TYPE;
  onClose?: () => void;
};
const CustomFieldsModal: FC<PROPS> = ({ source, usedOn, onClose }) => {
  const { hideModal } = useModal();

  const { data: fieldsData } = useGetDatas({ source });

  const sourceMap = DATA_SOURCE;
  const { data } = useGetViewDatas({
    type: usedOn,
    source: sourceMap[source],
  });
  const { mutate: create } = useCreateViewData();
  const { mutate: update } = useUpdateViewData();

  const storedDataIds = data?.data?.[0]?.data;
  const dataIds = useMemo(() => {
    if (fieldsData?.data && storedDataIds?.length) {
      return storedDataIds.map(({ dataId }) => {
        const record = fieldsData?.data.find((v) => v._id === dataId);
        return {
          label: record?.name,
          value: record?._id,
        };
      });
    } else {
      return [];
    }
  }, [storedDataIds, fieldsData?.data]);

  const initialValues: CUSTOM_FIELDS_CONFIGURATOR_FORM = {
    selected: dataIds as { label: string; value: string }[],
  };

  const handleSubmitInTable = (values: { label: string; value: string }[]) => {
    if (data?.data.length) {
      update({
        source: sourceMap[source],
        type: usedOn,
        data: values.map(({ value }, index) => ({
          dataId: value,
          sequence: index,
        })),
      });
    } else {
      create(
        {
          source: sourceMap[source],
          type: usedOn,
          data: values.map(({ value }, index) => ({
            dataId: value,
            sequence: index,
          })),
        },
        {
          onSuccess() {
            hideModal();
          },
        },
      );
    }
  };

  const handleSubmitInInfoPage = (
    values: { label: string; value: string }[],
  ) => {
    if (data?.data.length) {
      update({
        source: sourceMap[source],
        type: usedOn,
        data: values.map(({ value }, index) => ({
          dataId: value,
          sequence: index,
        })),
      });
    } else {
      create(
        {
          source: sourceMap[source],
          type: usedOn,
          data: values.map(({ value }, index) => ({
            dataId: value,
            sequence: index,
          })),
        },
        {
          onSuccess() {
            hideModal();
          },
        },
      );
    }
  };

  const handleSubmitInForm = (values: { label: string; value: string }[]) => {
    if (data?.data.length) {
      update(
        {
          source: sourceMap[source],
          type: usedOn,
          data: (values ?? []).map(({ value }, index) => ({
            dataId: value,
            sequence: index,
          })),
        },
        {
          onSuccess() {
            hideModal();
            onClose?.();
          },
        },
      );
    } else {
      create(
        {
          source: sourceMap[source],
          type: usedOn,
          data: (values ?? []).map(({ value }, index) => ({
            dataId: value,
            sequence: index,
          })),
        },
        {
          onSuccess() {
            hideModal();
            onClose?.();
          },
        },
      );
    }
  };

  const onSubmit = (
    values: CUSTOM_FIELDS_CONFIGURATOR_FORM,
    { setSubmitting }: FormikHelpers<CUSTOM_FIELDS_CONFIGURATOR_FORM>,
  ) => {
    switch (usedOn) {
      case VIEWDATA_TYPE.TABLE: {
        handleSubmitInTable(values.selected ?? []);
        break;
      }
      case VIEWDATA_TYPE.INFO_PAGE: {
        handleSubmitInInfoPage(values.selected ?? []);

        break;
      }
      case VIEWDATA_TYPE.FORM: {
        handleSubmitInForm(values.selected ?? []);
        break;
      }
    }
    setSubmitting(false);
    hideModal();
  };
  return (
    <Formik
      enableReinitialize
      onSubmit={onSubmit}
      initialValues={initialValues}
      validationSchema={CUSTOM_FIELDS_CONFIGURATOR_SCHEMA}
    >
      {() => <CustomFieldsForm source={source} onClose={onClose} />}
    </Formik>
  );
};

export default CustomFieldsModal;
